import { createRootRoute, Link, Outlet, useRouterState } from '@tanstack/react-router';
import { Button, EmptyState, PageHeader, SectionCard } from '@verydia/ui';
import { Compass } from 'lucide-react';
import { AuthGate, isPublicPath } from '../components/auth-gate.tsx';
import { ConsoleShell } from '../components/shell.tsx';

export const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFound,
});

function RootLayout() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  // Sign-in, callback, error and access-denied render bare. Wrapping them in the
  // shell would put navigation to organizations and audit logs on the page a
  // signed-out visitor sees first.
  if (isPublicPath(pathname)) {
    return (
      <AuthGate>
        <Outlet />
      </AuthGate>
    );
  }

  return (
    <AuthGate>
      <ConsoleShell>
        <Outlet />
      </ConsoleShell>
    </AuthGate>
  );
}

/**
 * Rendered inside the shell rather than as a bare page, so a mistyped URL leaves
 * the operator with their navigation and context intact instead of stranding them.
 */
function NotFound() {
  return (
    <div className="flex flex-col gap-5">
      {/*
        Every other route renders an h1. Without one here a screen-reader user
        landing on a mistyped URL gets a document with no top-level heading,
        which is both a WCAG 1.3.1 gap and disorienting.
      */}
      <PageHeader
        title="Page not found"
        description="That route does not exist in this console."
        /*
          No root crumb. A missing route belongs to nobody: it is not a vendor
          resource and not platform administration, so naming either above it
          would be a guess — and the wrong guess names a tenant.
        */
        breadcrumbs={[{ label: 'Not found' }]}
        linkComponent={Link}
      />

      <SectionCard flush>
        <EmptyState
          icon={<Compass />}
          title="Nothing here"
          description="The address may be mistyped, or it may belong to a later stage of Verydia Control. The navigation on the left lists everything that exists today."
          action={
            <Link to="/">
              <Button variant="primary">Back to Overview</Button>
            </Link>
          }
        />
      </SectionCard>
    </div>
  );
}
